import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2, CreditCard, List, PackagePlus, Pencil, Plus, Power, Save, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MembershipPurchase, Payment } from '../../../../../packages/contracts/src/index.js';
import type { ReactNode } from 'react';
import { ActionMenu } from '../../components/ui/action-menu';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { Input, Label, Select, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatCurrency, formatDate } from '../../shared/format';
import { businessDateInput, toDateInput } from '../../shared/listFilters';

export function MembershipsPage() {
  const queryClient = useQueryClient();
  const [screen, setScreen] = useState<'packages' | 'purchases'>('packages');
  const [subView, setSubView] = useState<'list' | 'create'>('list');
  const [packageForm, setPackageForm] = useState({ name: '', description: '', dayCount: '10', expirationDays: '30', price: '0' });
  const [editingPackageId, setEditingPackageId] = useState('');
  const [editPackageForm, setEditPackageForm] = useState({ name: '', description: '', dayCount: '10', expirationDays: '30', price: '0' });
  const [customerId, setCustomerId] = useState('');
  const [packageId, setPackageId] = useState('');
  const [dogDays, setDogDays] = useState<Record<string, number>>({});
  const [editingPurchaseId, setEditingPurchaseId] = useState('');
  const [editPurchaseForm, setEditPurchaseForm] = useState({ startsAt: '', expiresAt: '', status: 'active' as MembershipPurchase['status'] });
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const packagesQuery = useQuery({ queryKey: ['membership-packages'], queryFn: () => api.memberships.packages({ limit: 100 }) });
  const purchasesQuery = useQuery({ queryKey: ['membership-purchases'], queryFn: () => api.memberships.purchases({ limit: 100 }) });
  const paymentsQuery = useQuery({ queryKey: ['payments', 'memberships'], queryFn: () => api.payments.list({ limit: 100 }) });
  const customersQuery = useQuery({ queryKey: ['customers', 'memberships'], queryFn: () => api.customers.list({ limit: 100 }) });
  const allDogsQuery = useQuery({ queryKey: ['dogs', 'memberships'], queryFn: () => api.dogs.listAll() });
  const dogsQuery = useQuery({ queryKey: ['customer-dogs', customerId, 'memberships'], queryFn: () => api.customers.allDogs(customerId), enabled: Boolean(customerId) });
  const activePackages = (packagesQuery.data?.items ?? []).filter((item) => item.active !== false);
  const selectedPackage = activePackages.find((item) => item.id === packageId);
  const selectedDogs = (dogsQuery.data?.items ?? []).filter((dog) => (dogDays[dog.id] ?? 0) > 0);
  const allocatedDays = useMemo(() => Object.values(dogDays).reduce((sum, days) => sum + days, 0), [dogDays]);

  const createPackage = useMutation({
    mutationFn: () => api.memberships.createPackage({
      name: packageForm.name,
      description: packageForm.description || undefined,
      dayCount: Number(packageForm.dayCount || '0'),
      expirationDays: Number(packageForm.expirationDays || '0'),
      priceCents: Math.round(Number(packageForm.price || '0') * 100),
      active: true,
    }),
    onSuccess: async () => {
      setPackageForm({ name: '', description: '', dayCount: '10', expirationDays: '30', price: '0' });
      setSubView('list');
      await queryClient.invalidateQueries({ queryKey: ['membership-packages'] });
    },
  });

  const createPurchase = useMutation({
    mutationFn: () => api.memberships.createPurchase({
      packageId,
      customerId,
      startsAt: toDateInput(new Date()),
      dogAllocations: selectedDogs.map((dog) => ({ dogId: dog.id, days: dogDays[dog.id] ?? 0 })),
    }),
    onSuccess: async () => {
      setPackageId('');
      setDogDays({});
      setSubView('list');
      await queryClient.invalidateQueries({ queryKey: ['membership-purchases'] });
    },
  });
  const updatePackage = useMutation({
    mutationFn: ({ id, active }: { readonly id: string; readonly active: boolean }) => api.memberships.updatePackage(id, { active }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['membership-packages'] }),
  });
  const editPackage = useMutation({
    mutationFn: () => api.memberships.updatePackage(editingPackageId, {
      name: editPackageForm.name,
      description: editPackageForm.description || undefined,
      dayCount: Number(editPackageForm.dayCount || '0'),
      expirationDays: Number(editPackageForm.expirationDays || '0'),
      priceCents: Math.round(Number(editPackageForm.price || '0') * 100),
    }),
    onSuccess: async () => {
      setEditingPackageId('');
      await queryClient.invalidateQueries({ queryKey: ['membership-packages'] });
    },
  });
  const deletePackage = useMutation({
    mutationFn: api.memberships.removePackage,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['membership-packages'] });
    },
  });
  const editPurchase = useMutation({
    mutationFn: () => api.memberships.updatePurchase(editingPurchaseId, editPurchaseForm),
    onSuccess: async () => {
      setEditingPurchaseId('');
      await queryClient.invalidateQueries({ queryKey: ['membership-purchases'] });
    },
  });
  const deletePurchase = useMutation({
    mutationFn: api.memberships.removePurchase,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['membership-purchases'] }),
  });

  const setDogAllocation = (dogId: string, days: number) => setDogDays((current) => ({ ...current, [dogId]: Math.max(0, days) }));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Membresias</h1>
        <p className="mt-1 text-sm text-slate-500">Paquetes prepagados de entradas de guarderia por perro.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SegmentedControl value={screen} options={[{ id: 'packages', label: 'Paquetes' }, { id: 'purchases', label: 'Compras' }]} onChange={(nextScreen) => { setScreen(nextScreen); setSubView('list'); }} ariaLabel="Seccion de membresias" />
        <SegmentedControl value={subView} options={[{ id: 'list', label: 'Lista' }, { id: 'create', label: screen === 'packages' ? 'Crear paquete' : 'Nueva compra' }]} onChange={setSubView} ariaLabel="Vista de membresias" />
      </div>

      {subView === 'create' ? <section className="grid gap-5 xl:max-w-2xl">
        {screen === 'packages' ? <Card>
          <CardHeader><h2 className="text-sm font-semibold">Nuevo paquete</h2></CardHeader>
          <CardContent>
            <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); createPackage.mutate(); }}>
              <div className="grid gap-1.5"><Label htmlFor="membershipName">Nombre</Label><Input id="membershipName" value={packageForm.name} onChange={(event) => setPackageForm((current) => ({ ...current, name: event.target.value }))} required /></div>
              <div className="grid gap-1.5"><Label htmlFor="membershipDescription">Descripcion</Label><Textarea id="membershipDescription" value={packageForm.description} onChange={(event) => setPackageForm((current) => ({ ...current, description: event.target.value }))} /></div>
              <div className="grid gap-3 sm:grid-rows-3">
                <div className="grid gap-1.5"><Label htmlFor="membershipDays">Dias</Label><Input id="membershipDays" type="number" min="1" value={packageForm.dayCount} onChange={(event) => setPackageForm((current) => ({ ...current, dayCount: event.target.value }))} required /></div>
                <div className="grid gap-1.5"><Label htmlFor="membershipExpiration">Expira en dias</Label><Input id="membershipExpiration" type="number" min="1" value={packageForm.expirationDays} onChange={(event) => setPackageForm((current) => ({ ...current, expirationDays: event.target.value }))} required /></div>
                <div className="grid gap-1.5"><Label htmlFor="membershipPrice">Precio MXN</Label><Input id="membershipPrice" type="number" min="0" step="1" value={packageForm.price} onChange={(event) => setPackageForm((current) => ({ ...current, price: event.target.value }))} required /></div>
              </div>
              {createPackage.isError ? <p className="text-sm text-rose-600">{createPackage.error.message}</p> : null}
              <Button type="submit" disabled={createPackage.isPending}><PackagePlus className="h-4 w-4" aria-hidden="true" />Crear paquete</Button>
            </form>
          </CardContent>
        </Card> : null}

        {screen === 'purchases' ? <Card>
          <CardHeader><h2 className="text-sm font-semibold">Comprar membresia</h2></CardHeader>
          <CardContent className="grid gap-3">
            <div className="grid gap-1.5"><Label>Cliente</Label><CustomerSearchBox value={customerId} placeholder="Selecciona un cliente" onChange={(nextCustomerId) => { setCustomerId(nextCustomerId); setDogDays({}); }} /></div>
            <div className="grid gap-1.5">
              <Label htmlFor="membershipPackage">Paquete</Label>
              <Select id="membershipPackage" value={packageId} onChange={(event) => { setPackageId(event.target.value); setDogDays({}); }}>
                <option value="">Selecciona paquete</option>
                {activePackages.map((pkg) => <option key={pkg.id} value={pkg.id}>{pkg.name} - {pkg.dayCount} dias - {formatCurrency(pkg.priceCents)}</option>)}
              </Select>
            </div>
            {selectedPackage ? <p className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3 text-sm text-[#4f5d49]">Asigna hasta {selectedPackage.dayCount} dias entre los perros seleccionados. Asignados: {allocatedDays}.</p> : null}
            <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
              {!customerId ? <p className="text-sm text-slate-500">Selecciona un cliente primero.</p> : null}
              {customerId && (dogsQuery.data?.items ?? []).length === 0 ? <p className="text-sm text-slate-500">Este cliente no tiene perros registrados.</p> : null}
              {(dogsQuery.data?.items ?? []).map((dog) => (
                <div key={dog.id} className="grid gap-2 rounded-md bg-[#f8faf6] p-3 sm:grid-cols-[1fr_140px]">
                  <span className="text-sm font-medium text-[#334033]">{dog.name}</span>
                  <Input type="number" min="0" max={selectedPackage?.dayCount ?? 0} value={dogDays[dog.id] ?? 0} onChange={(event) => setDogAllocation(dog.id, Number(event.target.value || '0'))} />
                </div>
              ))}
            </div>
            {createPurchase.isError ? <p className="text-sm text-rose-600">{createPurchase.error.message}</p> : null}
            <Button type="button" disabled={!customerId || !packageId || selectedDogs.length === 0 || !selectedPackage || allocatedDays <= 0 || allocatedDays > selectedPackage.dayCount || createPurchase.isPending} onClick={() => createPurchase.mutate()}>
              <Save className="h-4 w-4" aria-hidden="true" />
              Guardar compra
            </Button>
          </CardContent>
        </Card> : null}
      </section> : null}

      {screen === 'packages' && subView === 'list' ? <Card>
        <CardHeader><h2 className="text-sm font-semibold">Paquetes</h2></CardHeader>
        <CardContent>
          {packagesQuery.isLoading ? <LoadingState label="Cargando paquetes" /> : null}
          {packagesQuery.isError ? <ErrorState message={packagesQuery.error.message} /> : null}
          {packagesQuery.data?.items.length === 0 ? <EmptyState title="Sin paquetes" body="Crea un paquete de membresia para vender entradas prepagadas." /> : null}
          {(packagesQuery.data?.items ?? []).length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <thead><tr><Th>Nombre</Th><Th>Dias</Th><Th>Expiracion</Th><Th>Precio</Th><Th>Estado</Th><Th>Acciones</Th></tr></thead>
              <tbody>{packagesQuery.data?.items.map((pkg) => (
                <tr key={pkg.id}>
                  <Td>{editingPackageId === pkg.id ? <Input value={editPackageForm.name} onChange={(event) => setEditPackageForm((current) => ({ ...current, name: event.target.value }))} /> : pkg.name}</Td>
                  <Td>{editingPackageId === pkg.id ? <Input type="number" min="1" value={editPackageForm.dayCount} onChange={(event) => setEditPackageForm((current) => ({ ...current, dayCount: event.target.value }))} /> : pkg.dayCount}</Td>
                  <Td>{editingPackageId === pkg.id ? <Input type="number" min="1" value={editPackageForm.expirationDays} onChange={(event) => setEditPackageForm((current) => ({ ...current, expirationDays: event.target.value }))} /> : `${pkg.expirationDays} dias`}</Td>
                  <Td>{editingPackageId === pkg.id ? <Input type="number" min="0" step="1" value={editPackageForm.price} onChange={(event) => setEditPackageForm((current) => ({ ...current, price: event.target.value }))} /> : formatCurrency(pkg.priceCents)}</Td>
                  <Td><Badge className={pkg.active === false ? 'bg-[#f3e5df] text-[#8a4a3a]' : 'bg-[#e5f3e4] text-[#2f6b38]'}>{pkg.active === false ? 'Inactivo' : 'Activo'}</Badge></Td>
                  <Td>
                    <div className="flex flex-wrap gap-2">
                      {editingPackageId === pkg.id ? (
                        <>
                          <Button type="button" variant="secondary" className="h-8 px-2.5" disabled={editPackage.isPending || !editPackageForm.name} onClick={() => editPackage.mutate()}><Save className="h-4 w-4" aria-hidden="true" />Guardar</Button>
                          <Button type="button" variant="ghost" className="h-8 px-2.5" onClick={() => setEditingPackageId('')}><X className="h-4 w-4" aria-hidden="true" />Cancelar</Button>
                        </>
                      ) : (
                        <>
                          <ActionMenu groups={[
                            {
                              label: 'Gestion',
                              children: <>
                                <MenuButton onClick={() => { setEditingPackageId(pkg.id); setEditPackageForm({ name: pkg.name, description: pkg.description ?? '', dayCount: String(pkg.dayCount), expirationDays: String(pkg.expirationDays), price: String(pkg.priceCents / 100) }); }} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar</MenuButton>
                                <MenuButton disabled={updatePackage.isPending} onClick={() => updatePackage.mutate({ id: pkg.id, active: pkg.active === false })} icon={<Power className="h-4 w-4" aria-hidden="true" />}>{pkg.active === false ? 'Activar' : 'Desactivar'}</MenuButton>
                              </>,
                            },
                            {
                              label: 'Riesgo',
                              tone: 'danger',
                              children: <MenuButton danger disabled={deletePackage.isPending} onClick={() => setConfirm({ title: 'Eliminar paquete', body: `Eliminar el paquete ${pkg.name}? Si ya tiene compras, el API lo bloqueara y deberas desactivarlo.`, confirmLabel: 'Eliminar', onConfirm: () => deletePackage.mutate(pkg.id) })} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}>Eliminar</MenuButton>,
                            },
                          ]} />
                        </>
                      )}
                    </div>
                    {editingPackageId === pkg.id ? <Textarea className="mt-2" value={editPackageForm.description} onChange={(event) => setEditPackageForm((current) => ({ ...current, description: event.target.value }))} placeholder="Descripcion" /> : null}
                  </Td>
                </tr>
              ))}</tbody>
            </Table>
            </div>
          ) : null}
          {updatePackage.isError ? <p className="mt-3 text-sm text-rose-600">{updatePackage.error.message}</p> : null}
          {editPackage.isError ? <p className="mt-3 text-sm text-rose-600">{editPackage.error.message}</p> : null}
          {deletePackage.isError ? <p className="mt-3 text-sm text-rose-600">{deletePackage.error.message}</p> : null}
        </CardContent>
      </Card> : null}

      {screen === 'purchases' && subView === 'list' ? <Card>
        <CardHeader><h2 className="text-sm font-semibold">Membresias vendidas</h2></CardHeader>
        <CardContent>
          {purchasesQuery.isLoading ? <LoadingState label="Cargando compras" /> : null}
          {purchasesQuery.isError ? <ErrorState message={purchasesQuery.error.message} /> : null}
          {(purchasesQuery.data?.items ?? []).length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <thead><tr><Th>Paquete</Th><Th>Cliente</Th><Th>Vigencia</Th><Th>Dias por perro</Th><Th>Uso</Th><Th>Pago</Th><Th>Acciones</Th></tr></thead>
              <tbody>{purchasesQuery.data?.items.map((purchase) => {
                const customer = customersQuery.data?.items.find((item) => item.id === purchase.customerId);
                const customerName = customer ? `${customer.firstName} ${customer.lastName}` : purchase.customerId;
                const paidCents = paidCentsForMembership(paymentsQuery.data?.items ?? [], purchase.id);
                const paid = paidCents >= purchase.priceCents && purchase.priceCents > 0;
                const displayStatus = membershipDisplayStatus(purchase);
                return (
                  <tr key={purchase.id}>
                    <Td>{purchase.packageName}</Td>
                    <Td>{customerName}</Td>
                    <Td>{editingPurchaseId === purchase.id ? (
                      <div className="grid min-w-40 gap-2">
                        <Input aria-label="Fecha de inicio" type="date" value={editPurchaseForm.startsAt} onChange={(event) => setEditPurchaseForm((current) => ({ ...current, startsAt: event.target.value }))} />
                        <Input aria-label="Fecha de expiracion" type="date" value={editPurchaseForm.expiresAt} onChange={(event) => setEditPurchaseForm((current) => ({ ...current, expiresAt: event.target.value }))} />
                      </div>
                    ) : <div className="grid gap-1"><span>{purchase.startsAt} a {purchase.expiresAt}</span>{displayStatus === 'expired' ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#a33f32]"><CalendarX2 className="h-3.5 w-3.5" aria-hidden="true" />Expiró el {formatDate(purchase.expiresAt)}</span> : null}</div>}</Td>
                    <Td>{purchase.dogAllocations.map((allocation) => `${dogName(allocation.dogId, allDogsQuery.data?.items ?? [])}: ${allocation.remainingDays}/${allocation.days}`).join(', ')}</Td>
                    <Td>{editingPurchaseId === purchase.id ? <Select aria-label="Estado" value={editPurchaseForm.status} onChange={(event) => setEditPurchaseForm((current) => ({ ...current, status: event.target.value as MembershipPurchase['status'] }))}><option value="active">Activa</option><option value="expired">Expirada</option><option value="depleted">Agotada</option></Select> : <Badge className={displayStatus === 'expired' ? 'bg-[#f7ded9] text-[#963b30]' : displayStatus === 'depleted' ? 'bg-[#fff3d6] text-[#7a5a16]' : 'bg-[#e5f3e4] text-[#2f6b38]'}>{displayStatus === 'expired' ? 'Expirada' : displayStatus === 'depleted' ? 'Agotada' : 'Activa'}</Badge>}</Td>
                    <Td><Badge className={paid ? 'bg-[#e5f3e4] text-[#2f6b38]' : paidCents > 0 ? 'bg-[#fff3d6] text-[#7a5a16]' : undefined}>{paid ? 'Pagada' : paidCents > 0 ? `Abono ${formatCurrency(paidCents)}` : 'Pendiente'}</Badge></Td>
                    <Td>{editingPurchaseId === purchase.id ? (
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="secondary" className="h-8 px-2.5" disabled={editPurchase.isPending || !editPurchaseForm.startsAt || !editPurchaseForm.expiresAt} onClick={() => editPurchase.mutate()}><Save className="h-4 w-4" aria-hidden="true" />Guardar</Button>
                        <Button type="button" variant="ghost" className="h-8 px-2.5" onClick={() => setEditingPurchaseId('')}><X className="h-4 w-4" aria-hidden="true" />Cancelar</Button>
                      </div>
                    ) : <ActionMenu groups={[
                      {
                        label: 'Gestion',
                        children: <>
                          <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={`/admin/payments?customerId=${encodeURIComponent(purchase.customerId)}&sourceType=membership&sourceId=${encodeURIComponent(purchase.id)}`}><CreditCard className="h-4 w-4" aria-hidden="true" />Ir a pagos</Link>
                          <MenuButton onClick={() => { setEditingPurchaseId(purchase.id); setEditPurchaseForm({ startsAt: purchase.startsAt, expiresAt: purchase.expiresAt, status: purchase.status }); }} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar</MenuButton>
                        </>,
                      },
                      {
                        label: 'Riesgo',
                        tone: 'danger',
                        children: <MenuButton danger disabled={deletePurchase.isPending} onClick={() => setConfirm({ title: 'Eliminar compra', body: `Eliminar la compra de ${purchase.packageName}? Sus pagos y visitas anteriores se conservaran en el historial.`, confirmLabel: 'Eliminar', onConfirm: () => deletePurchase.mutate(purchase.id) })} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}>Eliminar</MenuButton>,
                      },
                    ]} />}</Td>
                  </tr>
                );
              })}</tbody>
            </Table>
            </div>
          ) : <EmptyState title="Sin compras" body="Cuando vendas una membresia aparecera aqui." />}
          {editPurchase.isError ? <p className="mt-3 text-sm text-rose-600">{editPurchase.error.message}</p> : null}
          {deletePurchase.isError ? <p className="mt-3 text-sm text-rose-600">{deletePurchase.error.message}</p> : null}
        </CardContent>
      </Card> : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function MenuButton({ children, danger, disabled, icon, onClick }: { readonly children: ReactNode; readonly danger?: boolean; readonly disabled?: boolean; readonly icon: ReactNode; readonly onClick: () => void }) {
  return <button type="button" className={`flex items-center gap-2 rounded-md px-2 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${danger ? 'text-[#8a4a3a] hover:bg-[#fff8f8]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} disabled={disabled} onClick={onClick}>{icon}{children}</button>;
}

function dogName(dogId: string, dogs: Array<{ readonly id: string; readonly name: string }>) {
  return dogs.find((dog) => dog.id === dogId)?.name ?? dogId;
}

export function membershipDisplayStatus(purchase: MembershipPurchase, today = businessDateInput()): MembershipPurchase['status'] {
  if (purchase.status === 'expired' || purchase.expiresAt < today) return 'expired';
  if (purchase.status === 'depleted' || purchase.dogAllocations.every((allocation) => allocation.remainingDays <= 0)) return 'depleted';
  return 'active';
}

function paidCentsForMembership(payments: Payment[], membershipId: string) {
  return payments.reduce((total, payment) => {
    const itemTotal = payment.items
      .filter((item) => item.sourceType === 'membership' && item.sourceId === membershipId)
      .reduce((sum, item) => sum + item.amountCents * item.quantity, 0);
    if (itemTotal > 0) return total + itemTotal;
    return payment.sourceType === 'membership' && payment.sourceId === membershipId ? total + payment.totalCents : total;
  }, 0);
}
